import get from 'lodash/get';
import moment from 'moment';
import { Analytics } from '../../Analytics';
import {
  KYC_ERROR_CODES,
  ERR_ALREADY_REGISTERED
} from '../../components/WidgetPage/constants';
import { storageAvailable } from '../../helpers';

const nextRegisterSteps = {
  PARENT_ADD_CHILD: 'parent_add_child',
  PARENT_VERIFY_IDENTITY: 'parent_verify_identity',
  PARENT_VERIFY_DECLINED: 'parent_verify_declined',
  PARENT_SET_INITIAL_FUNDING: 'parent_set_initial_funding',
  PARENT_SET_BANK_ACCOUNT: 'parent_set_bank_account',
  PARENT_CONFIRM_ORDER: 'parent_confirm_order',
  CHILD_REQUEST_CARD: 'child_request_card',
  CHILD_PENDING_PARENT_COMPLETION: 'child_pending_parent_completion',
  CHILD_NON_APP_USER: 'child_non_app_user',
  COMPLETE_PLACEHOLDER: 'complete_placeholder',
  COMPLETE: 'complete',
  DEACTIVATED: 'deactivated'
};

const isNextRegisterStepPastVerification = current =>
  current === nextRegisterSteps.PARENT_SET_INITIAL_FUNDING ||
  current === nextRegisterSteps.PARENT_SET_BANK_ACCOUNT ||
  current === nextRegisterSteps.PARENT_CONFIRM_ORDER;

export const ERROR_MESSAGES = {
  defaultCheckPhoneFailure: 'Please use a device that supports SMS messaging.',
  loginFailureErrDeactivated:
    'Your account has been deactivated. Please call customer support at 1-888-483-2645.',
  loginFailureErrDeactivatedApp:
    'Oops! Your account is currently closed. To reactivate, please log in to the Greenlight app.',
  defaultKbaChallengeAnswerFailure:
    'Unable to validate your user at this time.',
  defaultGetPricingPlanFailure: 'Unable to get current pricing plan.',
  defaultUpgradePlanFailure:
    'Something went wrong with your plan upgrade. Contact customer support for assistance.'
};

export const trackRegistrationError = ({
  name = 'Registration Error Triggered',
  trackInRum = true,
  eventProps = {}
}) => {
  Analytics.trackEvent(name, { trackInRum, eventProps });
};

// eslint-disable-next-line no-case-declarations
export const getExtras = (action, actionName) => {
  try {
    return action.payload.data.extras
      ? action.payload.data.extras.find(extra => extra.action === actionName) ||
          null
      : action.payload.data.unresolvedErrors.find(
          unresolvedError => unresolvedError.action === actionName
        ) || null;
  } catch (e) {
    return null;
  }
};

// HACK: getSubmitKycErrorMessage is being used by the ANSWER_SUCCESS reducer.
// Ideally we would handle submit_kyc_questions errors in the ANSWER_FAILURE reducer.
// Making that change will require re-thinking some things in the backend.
// tracking this tech debt here: https://greenlightme.atlassian.net/browse/NET-2455
export const getSubmitKycErrorMessage = action => {
  const submitKycActionName = 'submit_kyc_questions';

  const submitKycQuestionsExtras = getExtras(action, submitKycActionName);
  if (!submitKycQuestionsExtras || !submitKycQuestionsExtras.code) {
    return null;
  }

  if (
    submitKycQuestionsExtras.status &&
    submitKycQuestionsExtras.status.includes('pass')
  ) {
    // know-your-customer internally uses a status 'passed', and externally responds with a status 'pass'.
    // This is just to be safe. If a submit_kyc_questions extra has a passing status, don't look for an error message.
    return null;
  }

  // find an error message that exists on both submitKycQuestionsExtras and in unresolvedErrors
  const { code, action: actionName } = submitKycQuestionsExtras;
  const error = (action.payload.data.unresolvedErrors || []).find(
    unresolvedError =>
      unresolvedError.code === code && unresolvedError.action === actionName
  );

  if (!error) {
    return null;
  }

  let errorMessage;
  switch (code) {
    case 'ERR_IDENTITY_VERIFICATION':
      errorMessage =
        'There was an error verifying your identity. Please try again later.';
      break;
    case 'ERR_ALLOWED_TIMEOUT_EXCEEDED':
      // this error code doesn't exist yet, but it could.
      // If we get this code from backend, we can provide a more user-friendly err msg to end user.
      errorMessage =
        'You took too long to answer these questions. Please try again.';
      break;
    default:
      // use whatever error message came from the backend
      errorMessage = error.message;
      break;
  }

  return errorMessage;
};

const hasUnresolvedErrors = action =>
  action.payload.data.unresolvedErrors &&
  action.payload.data.unresolvedErrors.length > 0;

export const getAnswerSuccessErrorAndMessage = action => {
  const extras =
    getExtras(action, 'prove_prefill') || getExtras(action, 'set_plan');

  const error = (extras && extras.errorMessage) || hasUnresolvedErrors(action);
  const message = extras
    ? extras.errorMessage
    : getSubmitKycErrorMessage(action) || '';

  return {
    error,
    message
  };
};

export const getErrorMessage = action =>
  typeof get(action.error, 'response.data.message') === 'object'
    ? action.error.response.data.message.message
    : get(action.error, 'response.data.message') ||
      'An unexpected error occurred. Please try again later.';

export const getInitializeSuccessResponse = (action, state) => {
  const error =
    hasUnresolvedErrors(action) &&
    action.payload.data.unresolvedErrors.some(
      err => err.code !== ERR_ALREADY_REGISTERED
    );

  const partialKYCError =
    hasUnresolvedErrors(action) &&
    action.payload.data.unresolvedErrors.some(
      err => err.code === KYC_ERROR_CODES.partial
    );

  const message = partialKYCError
    ? 'Please re-enter your information and try again'
    : state.message;

  return {
    error,
    message,
    loading: false,
    initializeLoading: false,
    questions: action.payload.data.nextQuestions,
    flowId: action.payload.data.flowId || state.flowId,
    groupId: action.payload.data.groupId || state.groupId,
    totalGroupsInFlow:
      action.payload.data.totalGroupsInFlow || state.totalGroupsInFlow,
    registrationToken:
      action.payload.data.registrationToken || state.registrationToken,
    unresolvedErrors: action.payload.data.unresolvedErrors || [],
    prefilledAnswers: action.payload.data.prefilled || {},
    completeUser: !!(
      action.payload.data.nextQuestions &&
      action.payload.data.nextQuestions.indexOf('complete') !== -1
    ),
    completeOnInit: !!(
      action.payload.data.nextQuestions &&
      action.payload.data.nextQuestions.indexOf('complete') !== -1 &&
      (!Array.isArray(action.payload.data.unresolvedErrors) ||
        !action.payload.data.unresolvedErrors.length) &&
      (!Array.isArray(action.payload.data.errors) ||
        !action.payload.data.errors.length)
    ),
    resetKBAFlow: !!(
      action.payload.data.unresolvedErrors &&
      action.payload.data.unresolvedErrors.length > 0 &&
      action.payload.data.unresolvedErrors.find(
        err =>
          err.code === KYC_ERROR_CODES.partial ||
          err.code === KYC_ERROR_CODES.challenge
      )
    ),
    stargatewayKey: action.payload.data.userJWT
      ? action.payload.data.userJWT
      : state.stargatewayKey
  };
};

export const getLoginSuccessResponse = (action, state) => {
  let nextRegStep = action.payload.data.user.nextRegisterStep;
  let completeOnInit = false;

  if (nextRegStep === 'deactivated') {
    trackRegistrationError({
      eventProps: {
        registration_step: 'login',
        error_message: ERROR_MESSAGES.loginFailureErrDeactivated
      }
    });

    return {
      ...state,
      loading: false,
      error: true,
      message: state.newDeactivatedErrorMsgEnabled
        ? ERROR_MESSAGES.loginFailureErrDeactivatedApp
        : ERROR_MESSAGES.loginFailureErrDeactivated,
      registeredUser: null
    };
  }

  if (action.payload.data.user.nextRegisterStep === 'complete') {
    completeOnInit = true;
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('userCompleted', true);
    }

    if (action.payload.data.user.role === 'child') {
      nextRegStep = 'complete_child';
    }
  }

  const isReturningMaxPlanFlowUser =
    !!get(state, 'featureToggles.planFlow.enabled') &&
    get(state, 'featureToggles.planFlow.variables.planName') === 'max' &&
    get(action, 'payload.data.user.pricingPlan.current.planName') === 'max' &&
    nextRegStep !== 'complete' &&
    nextRegStep !== 'deactivated';

  return {
    ...state,
    loading: false,
    error: false,
    message: null,
    nextRegStep,
    completeOnInit,
    role: action.payload.data.user.role,
    tempPhoneNumber: action.payload.data.user.phoneNumber,
    registeredUser: {
      ...action.payload.data,
      token: action.payload.data.token
    },
    stargatewayKey: action.payload.data.token
      ? action.payload.data.token
      : state.stargatewayKey,
    // Indicate we need to reload toggles (so that backend alias will work and use the new experimentation id).
    togglesDirty: true,
    showPlanFlowReturningUserMessage: isReturningMaxPlanFlowUser
  };
};

export const isCreditWaitlistEnabledForFlow = flowId =>
  flowId === 'parent_flow_6' || flowId === 'parent_flow_11';

export const setNextRegStepFromRole = (
  role,
  needsAccessRequest,
  proveMobileAuthState
) => {
  let nextRegisterStep;
  if (role === 'parent') {
    if (proveMobileAuthState === 'success') {
      nextRegisterStep = 'register';
    } else {
      nextRegisterStep = 'validate';
    }
  }
  if (role === 'child') {
    nextRegisterStep = 'complete_placeholder';
  }

  return {
    needsAccessRequest: role === 'parent',
    nextRegisterStep
  };
};

export const setNextRegStepFromDob = (dob, state) => {
  const age = moment().diff(moment(dob, 'MM/DD/YYYY'), 'years');
  const pmaSuccess = state.proveMobileAuthState === 'success';
  if (state.preregisteredUser)
    return {
      ...state,
      dob,
      nextRegStep: 'register'
    };
  return {
    ...state,
    dob,
    nextRegStep: age >= 13 ? 'validate' : 'cancel_age',
    needsAccessRequest: age >= 13,
    tempPhoneNumber: pmaSuccess ? null : state.tempPhoneNumber,
    regAccessToken: pmaSuccess ? null : state.regAccessToken
  };
};

export const checkPhoneSuccess = (action, state) => ({
  ...state,
  loading: false,
  error: false,
  nextRegStep:
    // Children over 13 that come thru PMA flow have already gone thru choose role and need to go straight to validate
    state.proveMobileAuthState === 'invalidated' && state.role === 'child'
      ? 'validate'
      : action.payload.data.action,
  regAccessToken: action.payload.accessToken,
  tempPhoneNumber: action.extras
});
